import React from 'react';

// Define the types of icons that we support
export type IconType =
  | 'pencil'
  | 'trash'
  | 'plus'
  | 'user-plus'
  | 'user-check'
  | 'user-x'
  | 'save'
  | 'close'
  | 'camera'
  | 'arrow-left'
  | 'arrow-right'
  | 'dumbbell'
  | 'trophy'
  | 'award'
  | 'book-open'
  | 'target'
  | 'first-aid'
  | 'clipboard-list'
  | 'clipboard'
  | 'calendar'
  | 'clock'
  | 'youtube'
  | 'info-circle'
  | 'info'
  | 'question-circle'
  | 'play'
  | 'pause'
  | 'stop'
  | 'settings'
  | 'refresh'
  | 'exercise'
  | 'training'
  | 'heart'
  | 'heart-filled'
  | 'comment'
  | 'tag'
  | 'user'
  | 'userPlus'
  | 'userCheck'
  | 'at'
  | 'medal'
  | 'bell'
  // Additional icons needed by EnhancedSearchResults and other components
  | 'search'
  | 'user-search'
  | 'video-off'
  // Achievement icons
  | 'calendar-check'
  | 'weight'
  | 'copy'
  | 'check-circle'
  | 'edit'
  | 'bookmark'
  | 'search'
  | 'loading'
  // Offline support icons
  | 'wifi'
  | 'wifi-off'
  // Heart rate settings icons
  | 'bluetooth'
  | 'check'
  | 'warning';

interface IconSVGProps {
  icon?: IconType | string;
  name?: string; // Alias for icon for compatibility
  className?: string;
  title?: string;
}

// SVG icon implementation using pure SVG elements
const IconSVG: React.FC<IconSVGProps> = ({ icon, name, className = '', title }) => {
  // Use name as fallback for icon (for compatibility)
  const iconName = icon || name || '';
  let svgPath = '';
  let strokeBased = false;
  
  // Define the SVG paths for each icon
  switch (iconName) {
    case 'pencil':
      svgPath = 'M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z';
      break;
    case 'trash':
      svgPath = 'M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z';
      break;
    case 'plus':
      svgPath = 'M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z';
      break;
    case 'user-plus':
      svgPath = 'M8 9a3 3 0 100-6 3 3 0 000 6zm0 2a6 6 0 00-6 6h12a6 6 0 00-6-6zm7-5a1 1 0 01-1-1V4a1 1 0 112 0v1a1 1 0 01-1 1zm0 3a1 1 0 01-1-1V8a1 1 0 112 0v1a1 1 0 01-1 1z';
      break;
    case 'user-check':
      svgPath = 'M9 6a3 3 0 11-6 0 3 3 0 016 0zm-6 9a6 6 0 1112 0h-1.5a4.5 4.5 0 10-9 0H3zm14-9a1 1 0 10-2 0v1a1 1 0 102 0V6zm-1 3a1 1 0 100 2h3a1 1 0 100-2h-3z';
      break;
    case 'user-x':
      svgPath = 'M9 6a3 3 0 11-6 0 3 3 0 016 0zm-6 9a6 6 0 1112 0h-1.5a4.5 4.5 0 10-9 0H3zm12.293-5.707a1 1 0 00-1.414 1.414l.707.707-.707.707a1 1 0 101.414 1.414l.707-.707.707.707a1 1 0 101.414-1.414l-.707-.707.707-.707a1 1 0 00-1.414-1.414l-.707.707-.707-.707z';
      break;
    case 'save':
      svgPath = 'M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z';
      break;
    case 'close':
      svgPath = 'M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z';
      break;
    case 'camera':
      svgPath = 'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h1.86a2 2 0 011.664.89l.812 1.22A2 2 0 0016.07 7H17a2 2 0 012 2v7a2 2 0 01-2 2H5a2 2 0 01-2-2V9zm9-1a1 1 0 100-2 1 1 0 000 2zm-5 6a4 4 0 118 0 4 4 0 01-8 0z';
      break;
    case 'arrow-left':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z';
      break;
    case 'arrow-right':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zM9.293 7.707a1 1 0 011.414-1.414l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L10.586 11H7a1 1 0 110-2h3.586l-1.293-1.293z';
      break;
    case 'dumbbell':
      svgPath = 'M5 4v2h10V4h2v2h1a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V7a1 1 0 011-1h1V4h2zm3 8h4v-2H8v2zM5 7H4v10h12V7h-1v2h-2V7H7v2H5V7z';
      break;
    case 'trophy':
      svgPath = 'M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 8a2 2 0 100 4h10a2 2 0 100-4h-3v-2h3a2 2 0 002-2V5a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2h2v2h-4v2H5z';
      break;
    case 'award':
      svgPath = 'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';
      break;
    case 'book-open':
      svgPath = 'M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z';
      break;
    case 'target':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a4 4 0 00-3.446 6.032l-2.261 2.26a1 1 0 101.414 1.415l2.261-2.261A4 4 0 1011 5z';
      break;
    case 'first-aid':
      svgPath = 'M3 6a3 3 0 013-3h10a3 3 0 013 3v8a3 3 0 01-3 3H6a3 3 0 01-3-3V6zm5 1H6v2h2v2h2V9h2V7h-2V5H8v2z';
      break;
    case 'clipboard-list':
      svgPath = 'M9 2a1 1 0 000 2h2a1 1 0 100-2H9zM4 6v12h12V6H4zm3 2h2v2H7V8zm0 4h2v2H7v-2zm10-4h-4v2h4V8zm0 4h-4v2h4v-2z';
      break;
    case 'clipboard':
      svgPath = 'M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zM6 7a1 1 0 012 0v8a1 1 0 11-2 0V7zm8-4a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1z';
      break;
    case 'calendar':
      svgPath = 'M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z';
      break;
    case 'clock':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z';
      break;
    case 'youtube':
      svgPath = 'M10 2a8 8 0 100 16 8 8 0 000-16zM5.032 12.236c-.138-.725-.138-2.265 0-2.99.138-.725.394-1.095 1.183-1.095 1.024-.073 3.537-.073 4.56 0 .79 0 1.046.37 1.184 1.095.138.725.138 2.265 0 2.99-.138.725-.394 1.095-1.183 1.095-1.024.073-3.537.073-4.56 0-.79 0-1.046-.37-1.184-1.095zM8.75 8.41v2.66l2.5-1.33-2.5-1.33z';
      break;
    case 'info-circle':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3a1 1 0 102 0V7zm-1 8a1 1 0 100-2 1 1 0 000 2z';
      break;
    case 'question-circle':
      svgPath = 'M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z';
      break;
    case 'play':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z';
      break;
    case 'pause':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 011-1h.01a1 1 0 110 2H8a1 1 0 01-1-1zm2 0a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z';
      break;
    case 'stop':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z';
      break;
    case 'settings':
      svgPath = 'M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z';
      break;
    case 'refresh':
      svgPath = 'M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z';
      break;
    case 'exercise':
      svgPath = 'M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 00-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z M9 11H3v5a2 2 0 002 2h4v-7zm2 7h4a2 2 0 002-2v-5h-6v7z';
      break;
    case 'training':
      svgPath = 'M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 8a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zm6-6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zm0 8a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z';
      break;
    case 'heart':
      svgPath = 'M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z';
      break;
    case 'heart-filled':
      svgPath = 'M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z';
      break;
    case 'comment':
      svgPath = 'M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z';
      break;
    case 'tag':
      svgPath = 'M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z';
      break;
    case 'user':
      svgPath = 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z';
      break;
    case 'userPlus':
      svgPath = 'M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z';
      break;
    case 'userCheck':
      svgPath = 'M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z';
      break;
    case 'at':
      svgPath = 'M14.243 5.757a6 6 0 10-.986 9.284 1 1 0 111.087 1.678A8 8 0 1118 10a3 3 0 01-4.8 2.401A4 4 0 1114 10a1 1 0 102 0c0-1.537-.586-3.07-1.757-4.243zM12 10a2 2 0 10-4 0 2 2 0 004 0z';
      break;
    case 'medal':
      svgPath = 'M5 3a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0118 15H2a1 1 0 01-.707-1.707l.804-.804L2.22 12H0a2 2 0 01-2-2V3a2 2 0 012-2h10zm10 8a1 1 0 100-2h-3a1 1 0 100 2h3zm-2-4a1 1 0 100-2h-4a1 1 0 100 2h4z';
      break;
    case 'bell':
      svgPath = 'M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z';
      break;
    case 'wifi':
      svgPath = 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0';
      strokeBased = true;
      break;
    case 'wifi-off':
      svgPath = 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0M3 3l18 18';
      strokeBased = true;
      break;
    case 'search':
      svgPath = 'M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z';
      break;
    case 'user-search':
      svgPath = 'M8 6a2 2 0 11-4 0 2 2 0 014 0zM8 11a4 4 0 10-8 0v1h8v-1zM13.586 11.586a3 3 0 114.242 4.242l-1.121 1.121a1 1 0 01-1.414-1.414l1.12-1.121a1 1 0 10-1.413-1.414l-1.121 1.121a1 1 0 11-1.414-1.414l1.121-1.121z';
      break;
    case 'video-off':
      svgPath = 'M2 6a2 2 0 012-2h6a2 2 0 012 2v2.172l2-2V4a1 1 0 112 0v3.172l3 3V6a1 1 0 00-1-1h-1a1 1 0 110-2h1a3 3 0 013 3v8a3 3 0 01-3 3H5.828l-2-2H4a2 2 0 01-2-2V6zm0 14.828l16-16 1.414 1.414-16 16L2 20.828z';
      break;
    case 'bookmark':
      svgPath = 'M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z';
      break;
    case 'loading':
      svgPath = 'M10 3v2a5 5 0 000 10v2a7 7 0 010-14zm0 12.5v2a3.5 3.5 0 100-7v-2a5.5 5.5 0 110 9z';
      break;
    case 'calendar-check':
      svgPath = 'M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6zm0 4a1 1 0 011 1 1 1 0 001.414 0l2-2a1 1 0 10-1.414-1.414L7 10.586l-.293-.293a1 1 0 10-1.414 1.414l1 1A1 1 0 007 12z';
      break;
    case 'weight':
      svgPath = 'M10 3a1 1 0 011 1v1h3a2 2 0 012 2v2.236a2 2 0 01-.586 1.414l-1 1A2 2 0 0114 12.236V14a2 2 0 01-2 2H8a2 2 0 01-2-2v-1.764a2 2 0 01-.586-1.414l-1-1A2 2 0 014 8.236V6a2 2 0 012-2h3V4a1 1 0 011-1zm0 4a1 1 0 100 2 1 1 0 000-2z';
      break;
    case 'copy':
      svgPath = 'M8 2a1 1 0 000 2h2a1 1 0 100-2H8z M3 5a2 2 0 012-2 1 1 0 000 2H5v7h6V5h-.5a1 1 0 100-2H13a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5z';
      break;
    case 'check-circle':
      svgPath = 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z';
      break;
    case 'edit':
      svgPath = 'M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z';
      break;
    case 'bluetooth':
      svgPath = 'M10 2a1 1 0 011 1v5.586l3.707-3.707a1 1 0 011.414 1.414L12.414 10l3.707 3.707a1 1 0 01-1.414 1.414L11 11.414V17a1 1 0 11-2 0v-5.586l-3.707 3.707a1 1 0 01-1.414-1.414L7.586 10 3.879 6.293a1 1 0 011.414-1.414L9 8.586V3a1 1 0 011-1z';
      break;
    case 'check':
      svgPath = 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z';
      break;
    case 'warning':
      svgPath = 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z';
      break;
    default:
      // Default icon or empty
      svgPath = '';
  }
  
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className || 'h-5 w-5'}
      viewBox="0 0 20 20"
      fill={strokeBased ? "none" : "currentColor"}
      stroke={strokeBased ? "currentColor" : undefined}
      strokeWidth={strokeBased ? "2" : undefined}
    >
      {title && <title>{title}</title>}
      {strokeBased ? (
        <path
          d={svgPath}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          fillRule="evenodd"
          d={svgPath}
          clipRule="evenodd"
        />
      )}
    </svg>
  );
};

export default IconSVG;